import {
  Badge,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react"
import { useQueries, useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { FiSearch, FiX } from "react-icons/fi"
import {
  ContragentListApi,
  type ContragentListItemDTO,
} from "../../../api/contragentList"
import {
  type ContragentResponseDTO,
  ContragentService,
} from "../../../api/contragents"

type Props = {
  label: string
  contragentRole: "SUPPLIER" | "MANUFACTURER" | "CUSTOMER"
  value: string[]
  onChange: (nextIds: string[]) => void
  helperText?: string
}

export default function ContragentsMultiSelect({
  label,
  contragentRole,
  value,
  onChange,
  helperText,
}: Props) {
  const picker = useDisclosure()
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const selectedRowBg = useColorModeValue("green.50", "green.900")
  const iconButtonBg = useColorModeValue("white", "whiteAlpha.50")
  const iconButtonBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const iconButtonColor = useColorModeValue("gray.600", "gray.200")
  const inputBg = useColorModeValue("white", "whiteAlpha.50")
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const inputColor = useColorModeValue("gray.800", "whiteAlpha.900")
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query.trim())
    }, 350)
    return () => window.clearTimeout(timeoutId)
  }, [query])

  const selectedContragents = useQueries({
    queries: value.map((id) => ({
      queryKey: ["contragents", "detail", id],
      queryFn: () => ContragentService.getById(id),
      enabled: Boolean(id),
    })),
  }) as Array<{ data?: ContragentResponseDTO }>

  const { data, isPending } = useQuery({
    queryKey: ["contragents", "multi-select", contragentRole, debouncedQuery],
    queryFn: () =>
      ContragentListApi.getContragentListPage({
        search: debouncedQuery || undefined,
        status: "ACTIVE",
        role: contragentRole,
        page: 1,
        page_size: 50,
        sort_by: "full_name",
        sort_dir: "asc",
      }),
    enabled: picker.isOpen,
  })

  const filtered = useMemo(
    () =>
      (data?.items ?? []).filter(
        (item: ContragentListItemDTO) =>
          Array.isArray(item.roles) && item.roles.includes(contragentRole),
      ),
    [data?.items, contragentRole],
  )

  const selectedIds = useMemo(() => new Set(value), [value])
  const selectedLabels = useMemo(
    () =>
      selectedContragents
        .map((result) => result.data)
        .filter((item): item is ContragentResponseDTO => Boolean(item))
        .map((item) => ({
          id: item.id,
          label: item.full_name,
        })),
    [selectedContragents],
  )

  useEffect(() => {
    if (!picker.isOpen) setQuery("")
  }, [picker.isOpen])

  const toggleSelection = (id: string) => {
    if (selectedIds.has(id)) {
      onChange(value.filter((current) => current !== id))
      return
    }
    onChange([...value, id])
  }

  return (
    <>
      <FormControl>
        <FormLabel>{label}</FormLabel>
        <VStack align="stretch" spacing={3}>
          <HStack>
            <Input
              readOnly
              value={selectedLabels.map((item) => item.label).join(", ")}
              placeholder="Nie wybrano kontrahentów"
              cursor="pointer"
              onClick={picker.onOpen}
              bg={inputBg}
              borderColor={inputBorder}
              color={inputColor}
              _readOnly={{
                bg: inputBg,
                borderColor: inputBorder,
                color: inputColor,
                cursor: "pointer",
              }}
            />
            <Tooltip label="Wybierz dostawców" hasArrow>
              <IconButton
                aria-label="Wybierz dostawców"
                icon={<FiSearch />}
                onClick={picker.onOpen}
                variant="outline"
                borderRadius="xl"
                bg={iconButtonBg}
                borderColor={iconButtonBorder}
                color={iconButtonColor}
              />
            </Tooltip>
            {value.length > 0 ? (
              <Tooltip label="Wyczyść dostawców" hasArrow>
                <IconButton
                  aria-label="Wyczyść dostawców"
                  icon={<FiX />}
                  variant="outline"
                  borderRadius="xl"
                  bg={iconButtonBg}
                  borderColor={iconButtonBorder}
                  color={iconButtonColor}
                  onClick={() => onChange([])}
                />
              </Tooltip>
            ) : null}
          </HStack>
          {selectedLabels.length > 0 ? (
            <Wrap>
              {selectedLabels.map((item) => (
                <WrapItem key={item.id}>
                  <Badge
                    px={2}
                    py={1}
                    borderRadius="md"
                    colorScheme="blue"
                    cursor="pointer"
                    onClick={() => toggleSelection(item.id)}
                  >
                    {item.label}
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          ) : null}
        </VStack>
        <FormHelperText>
          {helperText ?? "Możesz przypisać wielu kontrahentów do produktu."}
        </FormHelperText>
      </FormControl>

      <Modal
        isOpen={picker.isOpen}
        onClose={picker.onClose}
        size="5xl"
        scrollBehavior="inside"
        initialFocusRef={searchInputRef}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Wybierz kontrahentów</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3} align="stretch">
              <Input
                ref={searchInputRef}
                placeholder="Szukaj po nazwie, NIP, nazwie skróconej lub ID…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <Button onClick={() => setQuery("")}>Wyczyść</Button>
            </HStack>

            {isPending ? (
              <HStack justify="center" py={6}>
                <Spinner />
              </HStack>
            ) : (
              <TableContainer
                maxH="60vh"
                overflowY="auto"
                borderWidth="1px"
                borderRadius="md"
              >
                <Table size="sm" sx={{ tableLayout: "fixed" }}>
                  <Thead>
                    <Tr>
                      <Th w="320px">Nazwa</Th>
                      <Th w="140px">Nazwa skrócona</Th>
                      <Th w="140px">NIP</Th>
                      <Th w="140px">ID zewnętrzne</Th>
                      <Th w="140px">Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filtered.map((item) => {
                      const isSelected = selectedIds.has(item.id)
                      return (
                        <Tr
                          key={item.id}
                          _hover={{ bg: rowHoverBg }}
                          bg={isSelected ? selectedRowBg : undefined}
                          cursor="pointer"
                          onClick={() => toggleSelection(item.id)}
                        >
                          <Td
                            maxW="320px"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            title={item.full_name}
                          >
                            {item.full_name}
                          </Td>
                          <Td>{item.short_name ?? "—"}</Td>
                          <Td>{item.nip ?? "—"}</Td>
                          <Td>{item.external_id ?? "—"}</Td>
                          <Td>{isSelected ? "Wybrany" : "Dostępny"}</Td>
                        </Tr>
                      )
                    })}
                    {filtered.length === 0 ? (
                      <Tr>
                        <Td colSpan={5}>
                          <Box p={2} color="gray.500">
                            Brak wyników
                          </Box>
                        </Td>
                      </Tr>
                    ) : null}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={picker.onClose}>Gotowe</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
